import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { ThemeProperties } from "../../Theme/ThemeProperties";
import { Pagination } from "antd";
import AlertConfigCard from "../../Components/AlertConfigCard";
import TableSearch from "../../Features/Table/GenericDraggableTable/TableSearch";
import RecordCountBox from "../../Components/RecordCountBox";
import CartLoader from "../../Helper/Loaders/CartLoader";
import CODE from "../../Static/Constants/StatusCodes";
import { getAlertSettingsList, getAlertSeverityDetails, getAlertSearchSuggestions } from "../../Api/BmsFaultsApi";
import { transformAlertToCard, buildAlertListParams } from "./alertSettingsTransform";

const PAGE_SIZE = 10;

const SEARCH_FIELDS = [{ key: "alertName", label: "Alert Name", title: "Alert Name", isDefault: true }];

function AlertSettingsAlertList({ selectedFilters }) {
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerms, setSearchTerms] = useState({ terms: [], columnKey: "alertName" });
  const [currentPage, setCurrentPage] = useState(1);
  const prevFiltersSearchRef = useRef({ selectedFilters: null, searchTerms: null });

  const handleSearch = useCallback((terms, columnKey) => {
    setSearchTerms({ terms, columnKey: columnKey || "alertName" });
  }, []);

  const handlePageChange = useCallback((page) => setCurrentPage(page), []);

  const alertSearchSuggestionsHandler = useCallback(
    (typedValue, _columnKey, limit) => getAlertSearchSuggestions(typedValue, "alertDescription", limit),
    []
  );

  const fetchSeverityDetails = useCallback(async (alertId, severityLevelIDs, sevKey) => {
    const levelID = severityLevelIDs?.[sevKey];
    if (levelID == null) return null;
    const res = await getAlertSeverityDetails(alertId, levelID);
    if (res?.responseStatus?.code === CODE.SUCCESS) {
      const raw = res.response?.data?.[0];
      if (!raw) return null;
      return {
        description: raw.alertDescription,
        causeOfFault: raw.alertReason,
        instructions: raw.instructions?.length ? raw.instructions : null,
      };
    }
    return null;
  }, []);

  // Stable per-alert fetcher callbacks — only rebuilt when alert data changes,
  // not on every parent render, so AlertConfigCard's useCallback stays valid
  const alertFetchers = useMemo(
    () =>
      Object.fromEntries(
        alerts.map((a) => [
          a.id,
          (sevKey) => fetchSeverityDetails(a.id, a.severityLevelIDs, sevKey),
        ])
      ),
    [alerts, fetchSeverityDetails]
  );

  // Single effect handles both page-reset-on-filter-change and data fetch,
  // eliminating the double-fetch caused by two separate overlapping effects
  useEffect(() => {
    const prev = prevFiltersSearchRef.current;
    const filtersOrSearchChanged =
      prev.selectedFilters !== selectedFilters || prev.searchTerms !== searchTerms;
    prevFiltersSearchRef.current = { selectedFilters, searchTerms };

    // If filters/search changed and we're not on page 1, reset the page.
    // The resulting re-render triggers this effect again at page 1 — no fetch here.
    if (filtersOrSearchChanged && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    const page = filtersOrSearchChanged ? 1 : currentPage;
    let active = true;
    setLoading(true);

    getAlertSettingsList(buildAlertListParams(selectedFilters, searchTerms, page, PAGE_SIZE))
      .then((res) => {
        if (!active) return;
        if (res?.responseStatus?.code === CODE.SUCCESS) {
          setAlerts((res.response?.data || []).map(transformAlertToCard));
          setTotal(res.response?.total?.total ?? 0);
        } else {
          setAlerts([]);
          setTotal(0);
        }
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setAlerts([]);
        setTotal(0);
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [currentPage, selectedFilters, searchTerms]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", fontFamily: ThemeProperties.primaryFontV2 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "20px",
          flexShrink: 0,
          mb: "20px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Typography sx={{ fontSize: "21px", fontWeight: 700, color: "#1A1A24", lineHeight: "28px" }}>
            Alert List
          </Typography>
          {!loading && <RecordCountBox count={total} />}
        </Box>

        <TableSearch
          onSearch={handleSearch}
          searchableFields={SEARCH_FIELDS}
          searchSuggestionsHandler={alertSearchSuggestionsHandler}
        />
      </Box>

      {/* Alert cards + pagination */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "18px",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
            <CartLoader />
          </Box>
        ) : alerts.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
            <Typography sx={{ fontSize: "14px", color: "#9E9E9E" }}>No alerts found</Typography>
          </Box>
        ) : (
          alerts.map((alert) => (
            <AlertConfigCard
              key={alert.id}
              alert={alert}
              onFetchSeverityDetails={alertFetchers[alert.id]}
            />
          ))
        )}

        {!loading && total > 0 && (
          <Box sx={{ display: "flex", justifyContent: "right", pt: 1, pb: 1 }}>
            <Pagination
              showSizeChanger={false}
              current={currentPage}
              pageSize={PAGE_SIZE}
              total={total}
              onChange={handlePageChange}
              showTotal={(t, range) => `${range[0]}-${range[1]} / ${t}`}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

AlertSettingsAlertList.propTypes = {
  selectedFilters: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
};

export default AlertSettingsAlertList;
